'use strict';

import Routes from './../../utils/Routes';
import FluxEventEmitter from '../FluxEventEmitter';
import assign from 'object-assign';
import { doFetch, gtagHelper } from '../../utils/CommonUtils';
import Flux from '../../flux/Flux';
import EnumGA4Events from '../../utils/EnumGA4Events';
import { trackPurchase } from '../../utils/DataLayerHelper';
import Constants from '../Constants.js';
import TileGridStore from './TileGridStore.js';

const ChangeEvents = {
	Gallery: 'gallery',
	GalleryRating: 'galleryRating',
	PictureRating: 'pictureRating',
	Comments: 'comments',
	PinnedPhotos: 'pinnedPhotos',
	CurrentVideoTimeEvent: 'currentVideoTimeEvent',
	CurrentVideoCinematicModeEvent: 'currentVideoCinematicModeEvent',
	FocusCommentsInput: 'focusCommentsInput',
	EnableFeedback: 'enableFeedback',
	PauseVideo: 'pauseVideo',
};

const galleryData = {};
clearGalleryData();

const videoPlayerData = {
	currentVideoTime: 0,
	cinematicMode: false,
};

function clearGalleryData() {
	galleryData.albumData = [];
	galleryData.pictureData = [];
	galleryData.sedcardsData = [];
	galleryData.commentsData = [];
	galleryData.pinnedPhotosData = {};
	galleryData.previewData = {};
}

function initSedcardsData(actorId, initValues) {
	initValues = initValues || {};

	if (galleryData.sedcardsData[actorId] !== undefined) {
		return;
	}

	galleryData.sedcardsData[actorId] = {
		albumTotalViews: initValues.albumTotalViews || 0,
		guestRatingLikes: initValues.guestRatingLikes || 0,
		guestRatingDislikes: initValues.guestRatingDislikes || 0,
		isLiked: initValues.isLiked || false,
		isDisliked: initValues.isDisliked || false,
	};
}

function initPictureData(pictureId, initValues) {
	initValues = initValues || {};

	if (galleryData.pictureData[pictureId] !== undefined) {
		return;
	}

	galleryData.pictureData[pictureId] = {
		guestRatingLikes: initValues.guestRatingLikes || 0,
		guestRatingDislikes: initValues.guestRatingDislikes || 0,
		isLiked: initValues.isLiked || false,
		isDisliked: initValues.isDisliked || false,
	};
}

function getSedcards(actorId) {
	return doFetch(
		Routes.getRoute(Routes.Names.GALLERY_GET_SEDCARDS, { actorIdOrName: actorId }),
		{
			actorId: actorId,
		},
		Constants.HttpMethods.GET,
		true,
	);
}

function loadSedcards(actorId) {
	if (typeof galleryData.sedcardsData[actorId] === 'undefined' || !galleryData.sedcardsData[actorId].photos) {
		getSedcards(actorId).then(function (res) {
			if (res.data) {
				galleryData.sedcardsData[actorId] = res.data;
				GalleryStore.emitGalleryChange(actorId);
			}
		});
	} else {
		GalleryStore.emitGalleryChange(actorId);
	}
}

function setSedcardsGuestRating(actorId, guestRating) {
	doFetch(
		Routes.getRoute(Routes.Names.GALLERY_SET_SEDCARDS_GUEST_RATING, { actorId: actorId }),
		{
			guestRating: guestRating,
		},
		Constants.HttpMethods.POST,
		true,
	).then(function (res) {
		initSedcardsData(actorId);

		if (res.data && res.data.success) {
			galleryData.sedcardsData[actorId].albumTotalViews = res.data.albumTotalViews;
			galleryData.sedcardsData[actorId].guestRatingLikes = res.data.guestRatingLikes;
			galleryData.sedcardsData[actorId].guestRatingDislikes = res.data.guestRatingDislikes;
			galleryData.sedcardsData[actorId].isLiked = res.data.isLiked;
			galleryData.sedcardsData[actorId].isDisliked = res.data.isDisliked;
		}
		GalleryStore.emitGalleryRatingChange(actorId);
	});
}

function initCommentsData(albumId) {
	if (galleryData.commentsData[albumId] !== undefined) {
		return;
	}

	galleryData.commentsData[albumId] = {
		comments: [],
		commentsTotal: 0,
		isGuestAllowedToCommentAlbum: true,
	};
}

function initGalleryData(albumId, initValues) {
	initValues = initValues || {};

	if (galleryData.albumData[albumId] !== undefined) {
		return;
	}

	galleryData.albumData[albumId] = {
		albumTotalViews: initValues.albumTotalViews || 0,
		guestRatingLikes: initValues.guestRatingLikes || 0,
		guestRatingDislikes: initValues.guestRatingDislikes || 0,
		isLiked: initValues.isLiked || false,
		isDisliked: initValues.isDisliked || false,
	};
}

function getGalleryComments(albumId, limit, offset) {
	return doFetch(
		Routes.getRoute(Routes.Names.GALLERY_PRIVATE_SHOP_GET_COMMENTS),
		{
			albumId: albumId,
			limit: 500,
			offset: offset,
		},
		Constants.HttpMethods.GET,
		true,
	);
}

function postComment(albumId, commentText) {
	return doFetch(
		Routes.getRoute(Routes.Names.GALLERY_PRIVATE_SHOP_POST_COMMENT),
		{
			albumId: albumId,
			commentText: commentText,
		},
		Constants.HttpMethods.POST,
		true,
	);
}

function postGuestRating(albumId, guestRating) {
	return doFetch(
		Routes.getRoute(Routes.Names.GALLERY_PRIVATE_SHOP_SET_GUEST_RATING),
		{
			albumId: albumId,
			guestRating: guestRating,
		},
		Constants.HttpMethods.POST,
		true,
	);
}

function postPictureGuestRating(pictureId, guestRating) {
	return doFetch(
		Routes.getRoute(Routes.Names.GALLERY_SET_PICTURE_GUEST_RATING),
		{
			pictureId: pictureId,
			guestRating: guestRating,
		},
		Constants.HttpMethods.POST,
		true,
	);
}

function showMoreComments(albumId) {
	const isInitialized = galleryData.commentsData[albumId] !== undefined;

	initCommentsData(albumId);

	const comments = galleryData.commentsData[albumId].comments;
	const offset = comments.length;
	const limit = 5;

	getGalleryComments(albumId, limit, offset).then(function (res) {
		if (res.data && res.data.comments.length >= 0) {
			galleryData.commentsData[albumId].commentsTotal = res.data.total;
			galleryData.commentsData[albumId].comments = comments.concat(res.data.comments);
			galleryData.commentsData[albumId].isGuestAllowedToCommentAlbum = res.data.isGuestAllowedToCommentAlbum;
			GalleryStore.emitCommentsChange(albumId);
		} else if (res.data && !isInitialized) {
			GalleryStore.emitCommentsChange(albumId);
		}
	});
}

function setGuestRating(albumId, rating) {
	initGalleryData(albumId);

	postGuestRating(albumId, rating).then(function (res) {
		if (res.success && res.data && galleryData.albumData[albumId] !== undefined) {
			galleryData.albumData[albumId].albumTotalViews = res.data.albumTotalViews;
			galleryData.albumData[albumId].guestRatingLikes = res.data.guestRatingLikes;
			galleryData.albumData[albumId].guestRatingDislikes = res.data.guestRatingDislikes;
			galleryData.albumData[albumId].isLiked = res.data.isLiked;
			galleryData.albumData[albumId].isDisliked = res.data.isDisliked;

			GalleryStore.emitGalleryRatingChange(null, albumId);
		}
	});
}

function setPictureGuestRating(pictureId, rating) {
	initPictureData(pictureId);

	postPictureGuestRating(pictureId, rating).then(function (res) {
		if (res.success && res.data && galleryData.pictureData[pictureId] !== undefined) {
			galleryData.pictureData[pictureId].guestRatingLikes = res.data.guestRatingLikes;
			galleryData.pictureData[pictureId].guestRatingDislikes = res.data.guestRatingDislikes;
			galleryData.pictureData[pictureId].isLiked = res.data.isLiked;
			galleryData.pictureData[pictureId].isDisliked = res.data.isDisliked;

			GalleryStore.emitPictureRatingChange(pictureId);
		}
	});
}

function getGalleryData(albumId, params = {}) {
	const reqParams = params;
	reqParams.albumId = albumId;

	return doFetch(Routes.getRoute(Routes.Names.GALLERY), reqParams, Constants.HttpMethods.GET, true).then(function (res) {
		galleryData.albumData[albumId] = res.data;
		GalleryStore.emitGalleryChange(res.data && res.data.actor ? res.data.actor.id : null, albumId);
	});
}

function loadPinnedPhotosData(params = {}) {
	const key = getKey(params);
	const hasData = galleryData.pinnedPhotosData && typeof galleryData.pinnedPhotosData[key] !== 'undefined';

	if (!hasData) {
		doFetch(Routes.getRoute(Routes.Names.GALLERY_GET_PINNED_PHOTOS), params, Constants.HttpMethods.GET, true).then(function (res) {
			galleryData.pinnedPhotosData[key] = res.data;
			GalleryStore.emitPinnedPhotosChange();
		});
	} else {
		GalleryStore.emitPinnedPhotosChange();
	}
}

function updateGalleryData(albumId, useCache) {
	useCache = useCache || false;

	return new Promise((resolve) => {
		if (!useCache) {
			getGalleryData(albumId).then(() => {
				resolve();
			});
		} else {
			GalleryStore.emitGalleryChange(null, albumId);
			resolve();
		}
	});
}

function addComment(albumId, commentText) {
	postComment(albumId, commentText).then(function (res) {
		if (res.data) {
			initGalleryData(albumId);

			galleryData.commentsData[albumId].comments.unshift(res.data);
			galleryData.commentsData[albumId].commentsTotal += 1;
			galleryData.commentsData[albumId].isGuestAllowedToCommentAlbum = false;
			GalleryStore.emitCommentsChange(albumId);
		}
	});
}

function buyAlbum(albumId, { item_name = EnumGA4Events.DEFAULT_PARAMETER_ITEMS__ITEM_NAME__VALUE_PLATFORM_CONTENT } = {}) {
	const url = Routes.getRoute(Routes.Names.ALBUM_BUY).replace(':albumId', albumId);
	doFetch(url, null, Constants.HttpMethods.POST, true).then(function (result) {
		if (result.success && result.data.success) {
			if (typeof result.data.guestBalanceFormatted === 'string') {
				Flux.Guest.setBalanceFormatted(result.data.guestBalance, result.data.guestBalanceFormatted);
			}
			updateGalleryData(albumId, false).then(() => {
				const album = galleryData.albumData[albumId];
				if (album) {
					gtagHelper(EnumGA4Events.DEFAULT_PARAMETER_EVENT, EnumGA4Events.DEFAULT_EVENT_PURCHASE, {
						[EnumGA4Events.DEFAULT_PARAMETER_TRANSACTION_ID]: album.id + ':' + Flux.Guest.getId(), // required, pseudo transaction id
						[EnumGA4Events.DEFAULT_PARAMETER_CURRENCY]: EnumGA4Events.DEFAULT_PARAMETER_CURRENCY__VALUE_VXC,
						[EnumGA4Events.DEFAULT_PARAMETER_VALUE]: album.price,
						[EnumGA4Events.DEFAULT_PARAMETER_ITEMS]: [
							{
								[EnumGA4Events.DEFAULT_PARAMETER_ITEMS__ITEM_ID]: album.id,
								[EnumGA4Events.DEFAULT_PARAMETER_ITEMS__ITEM_BRAND]: album.actor.name,
								[EnumGA4Events.DEFAULT_PARAMETER_ITEMS__ITEM_NAME]: album.title,
							},
						],
					});

					trackPurchase({
						[EnumGA4Events.DEFAULT_PARAMETER_TRANSACTION_ID]: album.id + ':' + Flux.Guest.getId(), // required, pseudo transaction id
						[EnumGA4Events.DEFAULT_PARAMETER_VALUE]: album.price,
						[EnumGA4Events.DEFAULT_PARAMETER_ITEMS]: [
							{
								[EnumGA4Events.DEFAULT_PARAMETER_ITEM_ID]: album.id,
								[EnumGA4Events.DEFAULT_PARAMETER_ITEMS__ITEM_BRAND]: album.actor.name,
								[EnumGA4Events.DEFAULT_PARAMETER_ITEMS__ITEM_NAME]: item_name,
								[EnumGA4Events.DEFAULT_PARAMETER_ITEMS__ITEM_CATEGORY]:
									album.type === Flux.Constants.MediaTypes.VIDEO
										? EnumGA4Events.DEFAULT_PARAMETER_ITEMS__ITEM_CATEGORY__VALUE_VIDEO
										: EnumGA4Events.DEFAULT_PARAMETER_ITEMS__ITEM_CATEGORY__VALUE_IMAGE_ALBUM,
							},
						],
					});
				}
			});
		}
	});
}

function getKey(request) {
	const keys = [];
	for (const r in request) {
		if (Object.hasOwn(request, r)) {
			keys.push(r);
		}
	}

	keys.sort();

	const keyObject = {};
	for (let k = 0; k < keys.length; k++) {
		keyObject[keys[k]] = request[keys[k]];
	}

	return JSON.stringify(keyObject);
}

const GalleryStore = assign({}, FluxEventEmitter.prototype, {
	EVENTS: ChangeEvents,

	getGalleryData: function (albumId) {
		return galleryData.albumData[albumId];
	},

	getPictureData: function (pictureId) {
		return galleryData.pictureData[pictureId];
	},

	getCommentsData: function (albumId) {
		return galleryData.commentsData[albumId];
	},

	emitGalleryChange: function (actorId, albumId) {
		this.emit(ChangeEvents.Gallery, actorId, albumId);
	},

	emitGalleryRatingChange: function (actorId, albumId) {
		this.emit(ChangeEvents.GalleryRating, actorId, albumId);
	},

	emitPictureRatingChange: function (pictureId) {
		this.emit(ChangeEvents.PictureRating, pictureId);
	},

	emitCommentsChange: function (albumId) {
		this.emit(ChangeEvents.Comments, albumId);
	},

	getSedcards: function (actorId) {
		return galleryData.sedcardsData[actorId];
	},

	initGalleryData: function (albumId, data) {
		initGalleryData(albumId, data);
	},

	loadGallery: function (albumId, params) {
		getGalleryData(albumId, params);
	},

	emitPinnedPhotosChange: function () {
		this.emit(ChangeEvents.PinnedPhotos);
	},

	loadPinnedPhotos: function (params = {}) {
		loadPinnedPhotosData(params);
	},

	getPinnedPhotos: function (params = {}) {
		const key = getKey(params);
		return galleryData.pinnedPhotosData[key];
	},

	getCurrentVideoTime: function () {
		return videoPlayerData.currentVideoTime;
	},

	setCurrentVideoTime: function (time) {
		videoPlayerData.currentVideoTime = time;
		this.emit(ChangeEvents.CurrentVideoTimeEvent);
	},

	isCinematicModeEnabled: function () {
		return videoPlayerData.cinematicMode;
	},

	setCinematicModeEnabled: function (enabled) {
		videoPlayerData.cinematicMode = enabled;
		this.emit(ChangeEvents.CurrentVideoCinematicModeEvent, enabled);
	},

	focusCommentsInput: function () {
		this.emit(ChangeEvents.FocusCommentsInput);
	},

	enableFeedback: function (albumId) {
		this.emit(ChangeEvents.EnableFeedback, albumId);
	},

	pauseVideo: function () {
		this.emit(ChangeEvents.PauseVideo);
	},

	clearCache: function () {
		clearGalleryData();
	},

	// Direct methods (previously handled via actions)
	buyAlbum: function (albumId, trackerContext) {
		buyAlbum(albumId, trackerContext);
		TileGridStore.clearCache();
	},

	loadSedcards: function (actorId) {
		loadSedcards(actorId);
	},

	setSedcardsGuestRating: function (actorId, rating) {
		setSedcardsGuestRating(actorId, rating);
	},

	addComment: function (albumId, commentText) {
		addComment(albumId, commentText);
	},

	showMoreComments: function (albumId) {
		showMoreComments(albumId);
	},

	setGuestRating: function (albumId, rating) {
		setGuestRating(albumId, rating);
	},

	setPictureGuestRating: function (pictureId, rating) {
		setPictureGuestRating(pictureId, rating);
	},
});

export default GalleryStore;
